/**
 * Reportes y exportaciones.
 *
 * Las cifras las calcula el SERVIDOR y se muestran tal cual: la exportación a PDF y a
 * Excel usa el mismo cálculo, así que pantalla, PDF y Excel no pueden diferir.
 * Requiere conexión — un reporte consolidado no puede salir de una copia parcial.
 */

import { useCallback, useEffect, useState } from "react";
import {
  MAX_REPORT_STUDENTS,
  REPORT_LABELS,
  type ReportKind,
  type ReportResult,
} from "@senm/contracts";
import { formatInstant } from "@senm/domain";
import { ApiRequestError, apiDownload, apiRequest } from "../api/client.js";
import { openPdf } from "../services/payments.js";
import { db, normalizeForSearch, type LocalStudent } from "../local/db.js";
import type { StoredSession } from "../local/session.js";

const REPORTES = Object.keys(REPORT_LABELS) as ReportKind[];

export function ReportsPage(props: {
  session: StoredSession;
  conexion: string;
}): JSX.Element {
  const [tipo, setTipo] = useState<ReportKind>("PAGOS_POR_PERIODO");
  const [desde, setDesde] = useState("");
  /**
   * Boleta, cheque o número de transferencia.
   *
   * Es como se busca un pago que no fue en efectivo: el encargado llama diciendo
   * «deposité con la boleta 4512», no el código del alumno ni la fecha.
   */
  const [referencia, setReferencia] = useState("");
  const [hasta, setHasta] = useState("");
  /**
   * Estudiantes concretos que entran en el reporte.
   *
   * Vacío significa «todos», que es el caso corriente. Se llena cuando hace falta un
   * documento para un grupo suelto de alumnos que no forman familia ni comparten
   * grado: hermanos con apellidos distintos, un patrocinador que cubre a varios, un
   * grupo becado. Antes eso obligaba a emitir un reporte por alumno y graparlos.
   */
  const [alumnos, setAlumnos] = useState<LocalStudent[]>([]);
  const [busqueda, setBusqueda] = useState("");
  const [sugerencias, setSugerencias] = useState<LocalStudent[]>([]);
  const [resultado, setResultado] = useState<ReportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);

  /**
   * Fecha con la que se emite el reporte, modificable antes de exportar.
   *
   * Solo encabeza el PDF y el Excel: las cifras siguen siendo las del corte real,
   * que se imprime aparte. Separarlas evita que cambiar una fecha en pantalla
   * presente datos viejos como si fueran de hoy.
   */
  const [emitidoEl, setEmitidoEl] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );

  /**
   * Busca en el almacén LOCAL, no en el servidor.
   *
   * Los estudiantes ya están sincronizados en el dispositivo —es lo que usa la
   * pantalla de cobro— así que la lista responde mientras se escribe y sin pedir
   * nada. El reporte en sí sigue necesitando conexión; lo que no necesita red es
   * elegir a quién incluir.
   */
  useEffect(() => {
    const texto = busqueda.trim();
    if (texto === "") {
      setSugerencias([]);
      return;
    }
    let vigente = true;
    void (async () => {
      const aguja = normalizeForSearch(texto);
      const todos = await db.students.toArray();
      const elegidos = new Set(alumnos.map((alumno) => alumno.id));
      const encontrados = todos
        .filter(
          (alumno) =>
            !elegidos.has(alumno.id) &&
            (alumno.searchName.includes(aguja) ||
              alumno.code.toUpperCase().includes(aguja)),
        )
        .slice(0, 8);
      if (vigente) setSugerencias(encontrados);
    })();
    return () => {
      vigente = false;
    };
  }, [busqueda, alumnos]);

  const agregarAlumno = (alumno: LocalStudent): void => {
    setAlumnos((previos) =>
      previos.length >= MAX_REPORT_STUDENTS ||
      previos.some((otro) => otro.id === alumno.id)
        ? previos
        : [...previos, alumno],
    );
    setBusqueda("");
  };

  const quitarAlumno = (id: string): void => {
    setAlumnos((previos) => previos.filter((alumno) => alumno.id !== id));
  };

  const construirConsulta = useCallback((): string => {
    const parametros = new URLSearchParams();
    if (desde !== "") parametros.set("from", desde);
    if (hasta !== "") parametros.set("to", hasta);
    if (referencia.trim() !== "")
      parametros.set("reference", referencia.trim());
    // Separados por comas: el servidor descartaría los parámetros repetidos.
    if (alumnos.length > 0)
      parametros.set("studentIds", alumnos.map((a) => a.id).join(","));
    const cadena = parametros.toString();
    return cadena === "" ? "" : `?${cadena}`;
  }, [desde, hasta, alumnos, referencia]);

  const ejecutar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setResultado(
        await apiRequest<ReportResult>(
          `/v1/reports/${tipo}${construirConsulta()}`,
        ),
      );
    } catch (problema) {
      setResultado(null);
      setError(
        problema instanceof ApiRequestError && problema.isNetworkFailure
          ? "Los reportes necesitan conexión con el servidor."
          : "No fue posible generar el reporte.",
      );
    } finally {
      setCargando(false);
    }
  }, [tipo, construirConsulta]);

  useEffect(() => {
    void ejecutar();
  }, [ejecutar]);

  const exportar = async (formato: "pdf" | "xlsx"): Promise<void> => {
    const separador = construirConsulta() === "" ? "?" : "&";
    const emision = emitidoEl === "" ? "" : `&issuedAt=${emitidoEl}`;
    try {
      const archivo = await apiDownload(
        `/v1/reports/${tipo}${construirConsulta()}${separador}format=${formato}${emision}`,
      );
      openPdf(archivo.bytes, archivo.fileName);
    } catch {
      setError("No fue posible exportar el reporte.");
    }
  };

  return (
    <>
      <h1>Reportes</h1>
      <p className="ayuda">
        Las cifras provienen del servidor; el PDF y el Excel usan el mismo
        cálculo.
      </p>

      <div className="tarjeta">
        <label>
          <span>Reporte</span>
          <select
            value={tipo}
            onChange={(evento) => setTipo(evento.target.value as ReportKind)}
          >
            {REPORTES.map((item) => (
              <option key={item} value={item}>
                {REPORT_LABELS[item]}
              </option>
            ))}
          </select>
        </label>
        <div className="rejilla">
          <label>
            <span>Desde</span>
            <input
              type="date"
              value={desde}
              onChange={(evento) => setDesde(evento.target.value)}
            />
          </label>
          <label>
            <span>Hasta</span>
            <input
              type="date"
              value={hasta}
              onChange={(evento) => setHasta(evento.target.value)}
            />
          </label>
        </div>
        <fieldset className="modos">
          <legend>Estudiantes</legend>
          <p className="ayuda">
            {alumnos.length === 0
              ? "Sin seleccionar a nadie, el reporte incluye a todos los estudiantes."
              : `El reporte se limita a ${alumnos.length} estudiante(s). No tienen por qué ser familia.`}
          </p>

          {alumnos.length > 0 && (
            <ul className="fichas-seccion">
              {alumnos.map((alumno) => (
                <li key={alumno.id} className="ficha-seccion">
                  <span>
                    {alumno.code} · {alumno.fullName}
                  </span>
                  <button
                    type="button"
                    className="boton compacto"
                    onClick={() => quitarAlumno(alumno.id)}
                  >
                    Quitar
                    <span className="oculto-visual">
                      {" "}
                      a {alumno.fullName} del reporte
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <label>
            <span>Añadir estudiante por código o nombre</span>
            <input
              type="search"
              value={busqueda}
              onChange={(evento) => setBusqueda(evento.target.value)}
              placeholder="Código o nombre"
              disabled={alumnos.length >= MAX_REPORT_STUDENTS}
            />
          </label>

          {alumnos.length >= MAX_REPORT_STUDENTS && (
            <p className="ayuda">
              Máximo {MAX_REPORT_STUDENTS} estudiantes. Para grupos mayores
              conviene el reporte completo, sin seleccionar a nadie.
            </p>
          )}

          {busqueda.trim() !== "" && sugerencias.length === 0 && (
            <p className="ayuda">Ningún estudiante coincide.</p>
          )}

          {sugerencias.length > 0 && (
            <ul className="fichas-seccion">
              {sugerencias.map((alumno) => (
                <li key={alumno.id}>
                  <button
                    type="button"
                    className="boton compacto"
                    onClick={() => agregarAlumno(alumno)}
                  >
                    + {alumno.code} · {alumno.fullName}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </fieldset>

        <label>
          <span>Referencia de boleta, cheque o transferencia</span>
          <input
            type="search"
            value={referencia}
            onChange={(evento) => setReferencia(evento.target.value)}
            placeholder="BOL-4512"
          />
        </label>

        <label>
          <span>Fecha de emisión (encabeza el PDF y el Excel)</span>
          <input
            type="date"
            value={emitidoEl}
            onChange={(evento) => setEmitidoEl(evento.target.value)}
          />
        </label>

        <div className="pila">
          <button
            type="button"
            className="boton primario"
            onClick={() => void ejecutar()}
            disabled={cargando}
          >
            {cargando ? "Generando…" : "Generar"}
          </button>
          <button
            type="button"
            className="boton"
            onClick={() => void exportar("pdf")}
            disabled={resultado === null}
          >
            Exportar PDF
          </button>
          <button
            type="button"
            className="boton"
            onClick={() => void exportar("xlsx")}
            disabled={resultado === null}
          >
            Exportar Excel
          </button>
        </div>
      </div>

      {props.conexion === "SIN_CONEXION" && (
        <div className="aviso-caja" role="status">
          Sin conexión: los reportes consolidados no están disponibles ahora.
        </div>
      )}
      {error !== null && (
        <div className="aviso-caja error" role="alert">
          {error}
        </div>
      )}

      {resultado !== null && (
        <>
          <h2>{resultado.title}</h2>
          <p className="ayuda">
            Fecha de corte:{" "}
            {formatInstant(resultado.asOf, props.session.user.timeZone)}
          </p>

          {resultado.includesUnsynced && (
            <div className="aviso-caja" role="status">
              Este resultado incluye operaciones aún no sincronizadas. Las
              cifras pueden variar al confirmarse.
            </div>
          )}
          {resultado.truncated && (
            <div className="aviso-caja" role="status">
              El resultado fue truncado por límite de filas. Refine los filtros.
            </div>
          )}

          {resultado.rows.length === 0 ? (
            <p className="vacio">
              No se encontraron resultados con los filtros aplicados.
            </p>
          ) : (
            <div className="tarjeta tabla-desplazable">
              <table>
                <thead>
                  <tr>
                    {resultado.columns.map((columna) => (
                      <th
                        key={columna.key}
                        scope="col"
                        className={
                          columna.align === "derecha" ? "numero" : undefined
                        }
                      >
                        {columna.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resultado.rows.map((fila, indice) => (
                    <tr key={indice}>
                      {resultado.columns.map((columna) => (
                        <td
                          key={columna.key}
                          className={
                            columna.align === "derecha" ? "numero" : undefined
                          }
                        >
                          {String(fila[columna.key] ?? "")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {resultado.totals !== null && (
                  <tfoot>
                    <tr>
                      {resultado.columns.map((columna) => (
                        <th
                          key={columna.key}
                          scope={
                            columna.key === resultado.columns[0]?.key
                              ? "row"
                              : undefined
                          }
                          className={
                            columna.align === "derecha" ? "numero" : undefined
                          }
                        >
                          {String(resultado.totals?.[columna.key] ?? "")}
                        </th>
                      ))}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}
